import React, { useRef, useState } from 'react';
import IconButton from '@/ctComponents/IconButton';
import { fTimeDateUSFormat } from '@/actions/Helper';
import { PlannerAvailabilityTimeline } from './PlannerAvailabilityTimeline';
import { PlannerCardHeader } from './PlannerCardHeader';
import { PlannerCardKvColumn } from './PlannerCardKvColumn';
import { PlannerCardMovementPath } from './PlannerCardMovementPath';
import styles from './LiveUnloadPlannerCard.module.css';

const STRIP_MILESTONE_LABELS = new Set(['status', 'created', 'updated']);

function stripMetaFromMilestoneRows(rows) {
  return (rows || []).filter((row) => {
    const k = String(row?.label || '').trim().toLowerCase();
    return !STRIP_MILESTONE_LABELS.has(k);
  });
}

function getColumnGroups(card) {
  if (Array.isArray(card.columnGroups) && card.columnGroups.length > 0) {
    return card.columnGroups
      .filter((g) => g?.title !== 'Booking Result')
      .map((g) =>
        String(g?.title || '').trim() === 'Planner Milestones'
          ? { ...g, rows: stripMetaFromMilestoneRows(g.rows) }
          : g
      );
  }
  return [
    { title: 'Container Info', rows: card.containerInfo || [] },
    {
      title: 'Planner Milestones',
      rows: stripMetaFromMilestoneRows(card.milestones || []),
    },
  ];
}

export function LiveUnloadPlannerCard({ card }) {
  const timelineRef = useRef(null);
  const [activeTimelineItem, setActiveTimelineItem] = useState(null);
  const columnGroups = getColumnGroups(card);
  const kvColCount = Math.max(1, columnGroups.length);

  const createdText =
    fTimeDateUSFormat(card.createdAtIso) || card.createdAtFormatted || '';
  const updatedText =
    fTimeDateUSFormat(card.updatedAtIso) || card.updatedAtFormatted || '';

  const timelineFallbackStart = card.timeline?.windowStartLabel || '';
  const timelineFallbackEnd = card.timeline?.windowEndLabel || '';
  /** Same row as before under the track: API Created/Updated when present, else window labels (mock). */
  const axisLeft =
    createdText !== ''
      ? `Created ${createdText}`
      : timelineFallbackStart;
  const axisRight =
    updatedText !== ''
      ? `Updated ${updatedText}`
      : timelineFallbackEnd;

  return (
    <article className={styles.card}>
      <div className={styles.cardShell}>
        <div className={styles.cardLeft}>
          <PlannerCardHeader
            status={card.status}
            badges={card.badges}
            headerHint={card.headerHint}
          />

          <div className={styles.gridAndNavRow}>
            <div
              className={`${styles.gridKv} ${
                kvColCount === 2 ? styles.gridKvTwoCols : ''
              }`}
              style={{ '--kv-cols': kvColCount }}>
              {columnGroups.map((col, idx) => (
                <PlannerCardKvColumn
                  key={`${col.title}-${idx}`}
                  title={col.title}
                  rows={col.rows || []}
                />
              ))}
            </div>
            <div className={styles.timelineNavExternal}>
              <IconButton
                text="Prev"
                icon="leftArrow"
                color="#131517"
                border="1px solid #d7dde8"
                onClick={() => timelineRef.current?.goPrev?.()}
              />
              <div className={styles.timelineNavTitle}>
                {activeTimelineItem
                  ? `${activeTimelineItem.title} · ${activeTimelineItem.type}`
                  : 'Item'}
              </div>
              <IconButton
                text="Next"
                icon="rightArrow"
                color="#131517"
                border="1px solid #d7dde8"
                onClick={() => timelineRef.current?.goNext?.()}
              />
            </div>
          </div>

          <div className={styles.timelineWrap}>
            <PlannerAvailabilityTimeline
              ref={timelineRef}
              windowStartLabel={axisLeft}
              windowEndLabel={axisRight}
              nodes={card.timeline?.nodes || []}
              onActiveNavItemChange={setActiveTimelineItem}
            />
          </div>
        </div>

        {/* <PlannerCardMovementPath
          movementSegments={card.movementSegments}
          movementPath={card.movementPath}
        /> */}
      </div>
    </article>
  );
}

export {
  PlannerCardHeader,
  PLANNER_CARD_DEFAULT_HEADER_HINT,
} from './PlannerCardHeader';
export { PlannerCardKvColumn } from './PlannerCardKvColumn';
export { PlannerCardMovementPath } from './PlannerCardMovementPath';
export { PlannerAvailabilityTimeline } from './PlannerAvailabilityTimeline';
